import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { doorsApi } from "@/services/api";
import { apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhotoCapture } from "@/components/PhotoCapture";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// Post-repair verification sign-off (mirrors mobile maintenance/verify-repair).
// Records a pass/fail check of completed remedial works; a pass clears the
// door's pending_reinspection flag.
export function VerifyRepairDialog({ doorId, doorRef, open, onOpenChange, onDone }) {
  const [passed, setPassed] = useState(true);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await doorsApi.verifyRepair(doorId, { passed, note: note.trim() || undefined, photo: photo || undefined });
      toast.success(passed ? "Repair verified — door cleared" : "Marked as still failing — kept in the queue");
      onOpenChange(false);
      setNote(""); setPhoto(""); setPassed(true);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, "Could not save verification"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="verify-repair-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Verify repair</DialogTitle>
          <DialogDescription>Check the completed remedial works{doorRef ? ` on ${doorRef}` : ""} and record the outcome.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Outcome</Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPassed(true)} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-sm font-semibold ${passed ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 text-slate-600"}`} data-testid="verify-pass">
                <CheckCircle2 className="h-4 w-4" /> Repair passed
              </button>
              <button type="button" onClick={() => setPassed(false)} className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-sm font-semibold ${!passed ? "border-red-600 bg-red-600 text-white" : "border-slate-200 text-slate-600"}`} data-testid="verify-fail">
                <AlertTriangle className="h-4 w-4" /> Still failing
              </button>
            </div>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did you check?" className="mt-1" data-testid="verify-note" />
          </div>
          <div>
            <Label>Photo (optional)</Label>
            <div className="mt-1"><PhotoCapture photos={photo ? [photo] : []} onChange={(p) => setPhoto(p[0] || "")} max={1} testId="verify-photo" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="verify-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Save verification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
